import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * 利用者 API 操作クラス
 */
export default class ResidentGroups {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET ResidentGroups
   */
  public async get(userStore: LoginUser, groupId: number): Promise<ResidentGroupsGetModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ResidentGroup, formatParams);
    const response = await this.commonService.executeGet<ResidentGroupsGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET ResidentGroups
   */
  public async gets(
    userStore: LoginUser,
    queryParams?: { startIndex?: number; endIndex?: number; sortKey?: string; order?: string; targetFacilityId?: number }
  ): Promise<Array<ResidentGroupsGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ResidentGroups, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<ResidentGroupsGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET ResidentGroupsCount
   */
  public async getCount(userStore: LoginUser): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ResidentGroups, formatParams);
    // countの場合は末尾に「count」をつける
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * POST ResidentGroups
   */
  public async post(userStore: LoginUser, requestBody: ResidentGroupsPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ResidentGroups, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH ResidentGroups
   */
  public async patch(userStore: LoginUser, groupId: number, requestBody: ResidentGroupsPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ResidentGroup, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE Resident
   */
  public async delete(userStore: LoginUser, groupId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ResidentGroup, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface ResidentGroupsGetModel {
  facilityId: number;
  id: number;
  name: string;
  created: number;
  updated: number;
}

export interface ResidentGroupsPostModel {
  name: string;
}

export interface ResidentGroupsPatchModel {
  name: string;
  updated: number;
}
